<script setup>
import { computed, nextTick, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { picoSearch } from '@scmmishra/pico-search';
import SettingsLayout from 'dashboard/routes/dashboard/settings/SettingsLayout.vue';
import BaseSettingsHeader from 'dashboard/routes/dashboard/settings/components/BaseSettingsHeader.vue';
import BasePaywallModal from 'dashboard/routes/dashboard/settings/components/BasePaywallModal.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import packagesModule from 'shipping/store/modules/packages';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const { isCloudFeatureEnabled, isOnChatwootCloud, accountId } = useAccount();
const paywallI18nKey = computed(() =>
  isOnChatwootCloud.value ? 'PAYWALL' : 'ENTERPRISE_PAYWALL'
);
const isCartEnabled = computed(() => isCloudFeatureEnabled(FEATURE_FLAGS.CART));

const router = useRouter();
const openBilling = () => {
  router.push({
    name: 'billing_settings_index',
    params: { accountId: accountId.value },
  });
};

const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();

if (!store.hasModule('shippingPackages')) {
  store.registerModule('shippingPackages', packagesModule);
}

const PACKAGE_TYPES = computed(() => [
  { value: 'box', label: t('SHIPPING_PKG.TYPES.BOX') },
  { value: 'envelope', label: t('SHIPPING_PKG.TYPES.ENVELOPE') },
  { value: 'roll', label: t('SHIPPING_PKG.TYPES.ROLL') },
]);

const searchQuery = ref('');
const loading = ref({});

const selectedPackage = ref(null);
const addDialogRef = ref(null);
const editDialogRef = ref(null);
const deleteDialogRef = ref(null);

const emptyForm = () => ({
  name: '',
  package_type: 'box',
  width: '',
  height: '',
  length: '',
  diameter: '',
  weight: '',
  is_default: false,
});

const newForm = ref(emptyForm());
const editForm = ref(emptyForm());

const records = computed(() => store.getters['shippingPackages/getPackages']);
const uiFlags = computed(() => store.getters['shippingPackages/getUIFlags']);

const filteredRecords = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return records.value;
  return picoSearch(records.value, query, [{ name: 'name', weight: 4 }]);
});

const tableHeaders = computed(() => [
  t('SHIPPING_PKG.TABLE.NAME'),
  t('SHIPPING_PKG.TABLE.TYPE'),
  t('SHIPPING_PKG.TABLE.DIMENSIONS'),
  t('SHIPPING_PKG.TABLE.WEIGHT'),
  t('SHIPPING_PKG.TABLE.ACTIONS'),
]);

const typeLabel = type =>
  PACKAGE_TYPES.value.find(pt => pt.value === type)?.label ?? type;

const dimensionsSummary = pkg => {
  if (pkg.package_type === 'box') {
    return `${pkg.length} × ${pkg.width} × ${pkg.height} cm`;
  }
  if (pkg.package_type === 'envelope') {
    return `${pkg.length} × ${pkg.width} cm`;
  }
  return `${t('SHIPPING_PKG.FIELDS.LENGTH_SHORT')}: ${pkg.length} cm · ${t('SHIPPING_PKG.FIELDS.DIAMETER_SHORT')}: ${pkg.diameter} cm`;
};

const buildPayload = form => {
  const base = {
    name: form.name.trim(),
    package_type: form.package_type,
    weight: form.weight,
    length: form.length,
    is_default: form.is_default,
  };
  if (form.package_type === 'box') {
    base.width = form.width;
    base.height = form.height;
    base.diameter = null;
  } else if (form.package_type === 'envelope') {
    base.width = form.width;
    base.height = null;
    base.diameter = null;
  } else {
    base.width = null;
    base.height = null;
    base.diameter = form.diameter;
  }
  return base;
};

const isFormValid = form => {
  if (!form.name.trim() || !form.weight || !form.length) return false;
  if (form.package_type === 'box') return !!(form.width && form.height);
  if (form.package_type === 'envelope') return !!form.width;
  return !!form.diameter;
};

onBeforeMount(() => {
  store.dispatch('shippingPackages/get');
});

const openAddDialog = async () => {
  newForm.value = emptyForm();
  await nextTick();
  addDialogRef.value?.open();
};

const createPackage = async () => {
  if (!newForm.value.name.trim()) return;
  try {
    await store.dispatch(
      'shippingPackages/create',
      buildPayload(newForm.value)
    );
    addDialogRef.value?.close();
    useAlert(t('SHIPPING_PKG.CREATE_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.errors?.[0] || t('SHIPPING_PKG.CREATE_ERROR')
    );
  }
};

const openEditDialog = async pkg => {
  selectedPackage.value = pkg;
  editForm.value = {
    name: pkg.name,
    package_type: pkg.package_type,
    width: pkg.width ?? '',
    height: pkg.height ?? '',
    length: pkg.length ?? '',
    diameter: pkg.diameter ?? '',
    weight: pkg.weight ?? '',
    is_default: pkg.is_default ?? false,
  };
  await nextTick();
  editDialogRef.value?.open();
};

const onEditDialogClose = () => {
  selectedPackage.value = null;
};

const updatePackage = async () => {
  if (!editForm.value.name.trim() || !selectedPackage.value) return;
  try {
    await store.dispatch('shippingPackages/update', {
      id: selectedPackage.value.id,
      ...buildPayload(editForm.value),
    });
    editDialogRef.value?.close();
    useAlert(t('SHIPPING_PKG.UPDATE_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.errors?.[0] || t('SHIPPING_PKG.UPDATE_ERROR')
    );
  }
};

const openDeleteDialog = async pkg => {
  selectedPackage.value = pkg;
  await nextTick();
  deleteDialogRef.value?.open();
};

const onDeleteDialogClose = () => {
  selectedPackage.value = null;
};

const deletePackage = async () => {
  if (!selectedPackage.value) return;
  const pkgId = selectedPackage.value.id;
  loading.value[pkgId] = true;
  try {
    await store.dispatch('shippingPackages/delete', pkgId);
    deleteDialogRef.value?.close();
    useAlert(t('SHIPPING_PKG.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.errors?.[0] || t('SHIPPING_PKG.DELETE_ERROR')
    );
  } finally {
    loading.value[pkgId] = false;
  }
};
</script>

<template>
  <template v-if="!isCartEnabled">
    <div
      class="flex w-full items-center justify-center min-h-[calc(100vh-8rem)] px-6 py-12"
    >
      <BasePaywallModal
        feature-prefix="CHECKOUT_SETTINGS"
        :i18n-key="paywallI18nKey"
        :is-on-chatwoot-cloud="isOnChatwootCloud"
        @upgrade="openBilling"
      />
    </div>
  </template>
  <template v-else>
    <SettingsLayout
      :is-loading="uiFlags.isFetching"
      :loading-message="t('SHIPPING_PKG.LOADING')"
      :no-records-found="!records.length && !searchQuery"
      :no-records-message="t('SHIPPING_PKG.EMPTY')"
    >
      <template #header>
        <BaseSettingsHeader
          v-model:search-query="searchQuery"
          :title="t('SHIPPING_PKG.TITLE')"
          :description="t('SHIPPING_PKG.DESCRIPTION')"
          :search-placeholder="t('SHIPPING_PKG.SEARCH_PLACEHOLDER')"
          feature-name="shipping_packages"
        >
          <template v-if="records.length" #count>
            <span class="text-body-main text-n-slate-11">
              {{ t('SHIPPING_PKG.COUNT', { n: records.length }) }}
            </span>
          </template>
          <template v-if="isAdmin" #actions>
            <Button
              :label="t('SHIPPING_PKG.ADD')"
              size="sm"
              @click="openAddDialog"
            />
          </template>
        </BaseSettingsHeader>
      </template>

      <template #body>
        <BaseTable
          :headers="tableHeaders"
          :items="filteredRecords"
          :no-data-message="t('SHIPPING_PKG.NO_RESULTS')"
        >
          <template #header-4>
            <div class="flex justify-end">
              {{ t('SHIPPING_PKG.TABLE.ACTIONS') }}
            </div>
          </template>
          <template #row="{ items }">
            <BaseTableRow v-for="pkg in items" :key="pkg.id" :item="pkg">
              <template #default>
                <BaseTableCell>
                  <div class="flex items-center gap-2">
                    <span class="text-body-main text-n-slate-12">
                      {{ pkg.name }}
                    </span>
                    <span
                      v-if="pkg.is_default"
                      class="inline-flex items-center rounded-md bg-n-brand/10 px-1.5 py-0.5 text-xs font-medium text-n-brand"
                    >
                      {{ t('SHIPPING_PKG.DEFAULT_BADGE') }}
                    </span>
                  </div>
                </BaseTableCell>
                <BaseTableCell>
                  <span class="text-body-main text-n-slate-11">
                    {{ typeLabel(pkg.package_type) }}
                  </span>
                </BaseTableCell>
                <BaseTableCell>
                  <span class="text-body-main text-n-slate-11">
                    {{ dimensionsSummary(pkg) }}
                  </span>
                </BaseTableCell>
                <BaseTableCell>
                  <span class="text-body-main text-n-slate-11">
                    {{ pkg.weight }} {{ t('SHIPPING_PKG.WEIGHT_UNIT') }}
                  </span>
                </BaseTableCell>
                <BaseTableCell align="end">
                  <div
                    v-if="isAdmin"
                    class="flex gap-3 justify-end flex-shrink-0"
                  >
                    <Button
                      v-tooltip.top="t('SHIPPING_PKG.EDIT')"
                      icon="i-woot-edit-pen"
                      slate
                      sm
                      :is-loading="loading[pkg.id]"
                      @click="openEditDialog(pkg)"
                    />
                    <Button
                      v-tooltip.top="t('SHIPPING_PKG.DELETE')"
                      icon="i-woot-bin"
                      slate
                      sm
                      class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                      :is-loading="loading[pkg.id]"
                      @click="openDeleteDialog(pkg)"
                    />
                  </div>
                </BaseTableCell>
              </template>
            </BaseTableRow>
          </template>
        </BaseTable>
      </template>
    </SettingsLayout>
  </template>

  <!-- Add dialog -->
  <Dialog ref="addDialogRef" :title="t('SHIPPING_PKG.ADD_TITLE')">
    <div class="flex flex-col gap-4">
      <Input
        v-model="newForm.name"
        :label="t('SHIPPING_PKG.FORM.NAME_LABEL')"
        :placeholder="t('SHIPPING_PKG.FORM.NAME_PLACEHOLDER')"
        autocomplete="off"
        data-lpignore="true"
        data-1p-ignore="true"
      />
      <div class="w-full">
        <label>
          {{ t('SHIPPING_PKG.FORM.TYPE_LABEL') }}
          <select v-model="newForm.package_type">
            <option
              v-for="pt in PACKAGE_TYPES"
              :key="pt.value"
              :value="pt.value"
            >
              {{ pt.label }}
            </option>
          </select>
        </label>
      </div>
      <Input
        v-model="newForm.length"
        type="number"
        min="0"
        step="0.01"
        :label="t('SHIPPING_PKG.FORM.LENGTH_LABEL')"
        :placeholder="t('SHIPPING_PKG.FORM.MEASUREMENT_PLACEHOLDER')"
      />
      <Input
        v-if="
          newForm.package_type === 'box' || newForm.package_type === 'envelope'
        "
        v-model="newForm.width"
        type="number"
        min="0"
        step="0.01"
        :label="t('SHIPPING_PKG.FORM.WIDTH_LABEL')"
        :placeholder="t('SHIPPING_PKG.FORM.MEASUREMENT_PLACEHOLDER')"
      />
      <Input
        v-if="newForm.package_type === 'box'"
        v-model="newForm.height"
        type="number"
        min="0"
        step="0.01"
        :label="t('SHIPPING_PKG.FORM.HEIGHT_LABEL')"
        :placeholder="t('SHIPPING_PKG.FORM.MEASUREMENT_PLACEHOLDER')"
      />
      <Input
        v-if="newForm.package_type === 'roll'"
        v-model="newForm.diameter"
        type="number"
        min="0"
        step="0.01"
        :label="t('SHIPPING_PKG.FORM.DIAMETER_LABEL')"
        :placeholder="t('SHIPPING_PKG.FORM.MEASUREMENT_PLACEHOLDER')"
      />
      <Input
        v-model="newForm.weight"
        type="number"
        min="0"
        step="0.001"
        :label="t('SHIPPING_PKG.FORM.WEIGHT_LABEL')"
        :placeholder="t('SHIPPING_PKG.FORM.WEIGHT_PLACEHOLDER')"
      />
      <div
        class="flex h-10 items-center text-sm w-full gap-2 border border-solid border-n-strong px-3 py-1.5 rounded-xl justify-between"
      >
        <span class="text-n-slate-11">
          {{ t('SHIPPING_PKG.FORM.IS_DEFAULT_LABEL') }}
        </span>
        <Switch v-model="newForm.is_default" />
      </div>
    </div>
    <template #footer>
      <div class="flex justify-end gap-2 w-full">
        <Button variant="ghost" @click="addDialogRef?.close()">
          {{ t('SHIPPING_PKG.FORM.CANCEL') }}
        </Button>
        <Button
          :disabled="!isFormValid(newForm) || uiFlags.isCreating"
          :is-loading="uiFlags.isCreating"
          @click="createPackage"
        >
          {{ t('SHIPPING_PKG.FORM.CREATE') }}
        </Button>
      </div>
    </template>
  </Dialog>

  <!-- Edit dialog -->
  <Dialog
    ref="editDialogRef"
    :title="t('SHIPPING_PKG.EDIT_TITLE')"
    @close="onEditDialogClose"
  >
    <div class="flex flex-col gap-4">
      <Input
        v-model="editForm.name"
        :label="t('SHIPPING_PKG.FORM.NAME_LABEL')"
        :placeholder="t('SHIPPING_PKG.FORM.NAME_PLACEHOLDER')"
        autocomplete="off"
        data-lpignore="true"
        data-1p-ignore="true"
      />
      <div class="w-full">
        <label>
          {{ t('SHIPPING_PKG.FORM.TYPE_LABEL') }}
          <select v-model="editForm.package_type">
            <option
              v-for="pt in PACKAGE_TYPES"
              :key="pt.value"
              :value="pt.value"
            >
              {{ pt.label }}
            </option>
          </select>
        </label>
      </div>
      <Input
        v-model="editForm.length"
        type="number"
        min="0"
        step="0.01"
        :label="t('SHIPPING_PKG.FORM.LENGTH_LABEL')"
        :placeholder="t('SHIPPING_PKG.FORM.MEASUREMENT_PLACEHOLDER')"
      />
      <Input
        v-if="
          editForm.package_type === 'box' ||
          editForm.package_type === 'envelope'
        "
        v-model="editForm.width"
        type="number"
        min="0"
        step="0.01"
        :label="t('SHIPPING_PKG.FORM.WIDTH_LABEL')"
        :placeholder="t('SHIPPING_PKG.FORM.MEASUREMENT_PLACEHOLDER')"
      />
      <Input
        v-if="editForm.package_type === 'box'"
        v-model="editForm.height"
        type="number"
        min="0"
        step="0.01"
        :label="t('SHIPPING_PKG.FORM.HEIGHT_LABEL')"
        :placeholder="t('SHIPPING_PKG.FORM.MEASUREMENT_PLACEHOLDER')"
      />
      <Input
        v-if="editForm.package_type === 'roll'"
        v-model="editForm.diameter"
        type="number"
        min="0"
        step="0.01"
        :label="t('SHIPPING_PKG.FORM.DIAMETER_LABEL')"
        :placeholder="t('SHIPPING_PKG.FORM.MEASUREMENT_PLACEHOLDER')"
      />
      <Input
        v-model="editForm.weight"
        type="number"
        min="0"
        step="0.001"
        :label="t('SHIPPING_PKG.FORM.WEIGHT_LABEL')"
        :placeholder="t('SHIPPING_PKG.FORM.WEIGHT_PLACEHOLDER')"
      />
      <div
        class="flex h-10 items-center text-sm w-full gap-2 border border-solid border-n-strong px-3 py-1.5 rounded-xl justify-between"
      >
        <span class="text-n-slate-11">
          {{ t('SHIPPING_PKG.FORM.IS_DEFAULT_LABEL') }}
        </span>
        <Switch v-model="editForm.is_default" />
      </div>
    </div>
    <template #footer>
      <div class="flex justify-end gap-2 w-full">
        <Button variant="ghost" @click="editDialogRef?.close()">
          {{ t('SHIPPING_PKG.FORM.CANCEL') }}
        </Button>
        <Button
          :disabled="!isFormValid(editForm) || uiFlags.isUpdating"
          :is-loading="uiFlags.isUpdating"
          @click="updatePackage"
        >
          {{ t('SHIPPING_PKG.FORM.UPDATE') }}
        </Button>
      </div>
    </template>
  </Dialog>

  <!-- Delete dialog -->
  <Dialog
    ref="deleteDialogRef"
    type="alert"
    :title="t('SHIPPING_PKG.DELETE_TITLE')"
    :description="
      t('SHIPPING_PKG.DELETE_CONFIRMATION', {
        name: selectedPackage?.name ?? '',
      })
    "
    @close="onDeleteDialogClose"
  >
    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          variant="faded"
          color="slate"
          :label="t('SHIPPING_PKG.FORM.CANCEL')"
          class="w-full"
          type="button"
          :disabled="uiFlags.isDeleting"
          @click="deleteDialogRef?.close()"
        />
        <Button
          color="ruby"
          :label="t('SHIPPING_PKG.FORM.DELETE')"
          class="w-full"
          :is-loading="uiFlags.isDeleting"
          :disabled="uiFlags.isDeleting"
          type="submit"
          @click="deletePackage"
        />
      </div>
    </template>
  </Dialog>
</template>
